import { authStore } from "@bebit/auth";
import { DatePickerPreset } from "@bebit/types";
import { isHighlightablePreset } from "@bebit/utils";
import { useSessionStorage, useLocalStorage } from "@vueuse/core";
import { computed, ComputedRef } from "vue";

type UserId = string;

type PresetLocalStorage = Map<UserId, DatePickerPreset | undefined>;
type PresetSessionStorage = Map<UserId, DatePickerPreset | undefined>;

export const STORAGE_KEY = "JOURNEY_PRESET";

export function usePresetFromStorages() {
  const { presetFromLocal, savePresetToLocal } = usePresetFromLocal();
  const { presetFromSession, savePresetToSession } = usePresetFromSession();

  const savePresetToStorages = (newPreset: DatePickerPreset | undefined) => {
    savePresetToLocal(newPreset);
    savePresetToSession(newPreset);
  };

  const presetFromStorages: ComputedRef<DatePickerPreset | undefined> = computed(() => {
    if (presetFromSession.value) return presetFromSession.value;
    return presetFromLocal.value;
  });

  return { presetFromStorages, savePresetToStorages, presetFromLocal, presetFromSession };
}

function usePresetFromLocal() {
  const local = useLocalStorage<PresetLocalStorage>(STORAGE_KEY, new Map<UserId, DatePickerPreset>());

  const savePresetToLocal = (newPreset: DatePickerPreset | undefined) => {
    if (isHighlightablePreset(newPreset)) local.value.set(getCurrentUserId(), newPreset);
    else local.value.set(getCurrentUserId(), undefined);
  };

  const presetFromLocal: ComputedRef<DatePickerPreset | undefined> = computed(() => {
    return local.value.get(getCurrentUserId());
  });

  return { presetFromLocal, savePresetToLocal };
}

function usePresetFromSession() {
  const session = useSessionStorage<PresetSessionStorage>(STORAGE_KEY, new Map<UserId, DatePickerPreset | undefined>());

  const savePresetToSession = (newPreset: DatePickerPreset | undefined) => {
    if (isHighlightablePreset(newPreset)) session.value.set(getCurrentUserId(), newPreset);
    else session.value.set(getCurrentUserId(), undefined);
  };

  const presetFromSession: ComputedRef<DatePickerPreset | undefined> = computed(() => {
    return session.value.get(getCurrentUserId());
  });

  return { presetFromSession, savePresetToSession };
}

function getCurrentUserId(): string {
  return authStore.user ? authStore.user?.id.toString() : "";
}
